import React, { useContext, useEffect, useState } from "react";
import { Container, Row, Col, Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import {
  FaShoppingBag,
  FaClock,
  FaHeart,
  FaMoneyBillWave,
  FaBox,
  FaStar,
  FaShippingFast,
  FaExclamationTriangle,
  FaDownload,
  FaPlus,
  FaEye,
  FaTicketAlt,
  FaEdit,
  FaTimes,
  FaCheckCircle,
} from "react-icons/fa";
import Button from "../../../components/general/button/Button";
import Pagination from "../../../components/general/pagination/Pagination";
import { SellerDashboardSkeleton } from "../../../components/general/skeleton/SkeletonBlock";
import "../../../assets/css/seller/dashboard/SellerDashboard.css";
import ActionDropdown from "../../../components/seller/dropdowns/action/ActionDropdowns";
import Swal from "sweetalert2";
import SellerLayout from "../../../layouts/SellerLayout";
import MyContext from "../../../context/MyContext";
import axios from "axios";

// Inline Verification Modal Component (KYC intro)
const VerificationModal = ({ show, onHide, onSkip }) => {
  const navigate = useNavigate();

  const handleVerifyNow = () => {
    onHide();
    navigate("/seller/settings", { state: { activeTab: "verification" } });
  };

  const handleSkip = () => {
    localStorage.setItem("sellerSkippedVerification", "true");
    onSkip();
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
      className="verification-modal"
    >
      <Modal.Body className="verification-modal-body">
        <button className="verification-close-btn" onClick={handleSkip}>
          <FaTimes />
        </button>

        <div className="verification-icon">
          <FaCheckCircle />
        </div>

        <h2 className="verification-title">Complete Your Verification</h2>

        <p className="verification-text">
          To start selling on KisaanMart, please complete your KYC verification.
          This helps us ensure a safe and trusted marketplace for all users.
        </p>

        <div className="verification-benefits">
          <h3>Benefits of Verification:</h3>
          <ul>
            <li>✅ Add and manage products</li>
            <li>✅ Receive customer orders</li>
            <li>✅ Withdraw earnings</li>
            <li>✅ Build customer trust</li>
          </ul>
        </div>

        <div className="verification-actions">
          <Button
            variant="gradient"
            onClick={handleVerifyNow}
            className="verify-now-btn"
          >
            Verify Now
          </Button>
          <Button onClick={handleSkip} className="skip-btn">
            Skip for Now
          </Button>
        </div>

        <p className="verification-note">
          You can complete verification anytime from Settings → Documents
        </p>
      </Modal.Body>
    </Modal>
  );
};

const SellerDashboard = () => {
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [lowStockPage, setLowStockPage] = useState(1);
  const [lowStockPerPage, setLowStockPerPage] = useState(5);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const { baseUrl, authToken, loggedInSeller } = useContext(MyContext);

  // FIXED: pehle ye check sirf localStorage ("sellerVerified") pe hota
  // tha, jo sirf LOGIN ke waqt set hota hai. Agar seller login rahay
  // (session na tootay) aur admin uske baad KYC approve/reject kar de,
  // to ye localStorage value stale reh jati thi — modal seller ko baar
  // baar "verify karo" bolta rehta, chahe wo already verified ho chuka
  // ho. Ab dashboard mount hote hi /seller/profile/viewProfile (yehi
  // route jo DB se kmkyc.isVerified deta hai) se fresh status check
  // hota hai, localStorage sirf sync ke liye update hoti hai.
  const checkVerificationStatus = () => {
    if (!authToken) return;

    axios
      .post(
        `${baseUrl}/seller/profile/viewProfile`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } },
      )
      .then((response) => {
        if (response.data.success) {
          const isVerified =
            (response.data.seller?.kmkyc?.isVerified ?? null) === "verified";

          // localStorage ko fresh value se sync rakhte hain taake login
          // screen wala flag bhi aage se up-to-date rahe
          localStorage.setItem("sellerVerified", isVerified ? "true" : "false");

          const hasSkipped =
            localStorage.getItem("sellerSkippedVerification") === "true";

          if (!isVerified && !hasSkipped) {
            setShowVerificationModal(true);
          } else {
            setShowVerificationModal(false);
          }
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    checkVerificationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // Data states
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState({
    monthlyEarnings: 0,
    activeOrders: 0,
    totalProducts: 0,
    customerRating: 0,
  });
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [topProducts, setTopProducts] = useState([]);

  // ── Stats card config — icon/color yahin static hain, value API se aati hai ──
  const stats = [
    {
      label: "Monthly Earnings",
      value: `PKR ${Number(statsData.monthlyEarnings).toLocaleString()}`,
      icon: <FaMoneyBillWave />,
      bgColor: "#d1fae5",
    },
    {
      label: "Active Orders",
      value: statsData.activeOrders,
      icon: <FaShippingFast />,
      bgColor: "#dbeafe",
    },
    {
      label: "Total Products",
      value: statsData.totalProducts,
      icon: <FaBox />,
      bgColor: "#fef3c7",
    },
    {
      label: "Customer Rating",
      value: `${statsData.customerRating} / 5.0`,
      icon: <FaStar />,
      bgColor: "#fee2e2",
    },
  ];

  // ─── Fetch Dashboard Data ───────────────────────────────────────────────
  const getDashboardStats = () => {
    axios
      .get(`${baseUrl}/seller/dashboard`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        const data = response.data;
        setStatsData({
          monthlyEarnings: data.stats?.monthly_earnings ?? 0,
          activeOrders: data.stats?.active_orders ?? 0,
          totalProducts: data.stats?.total_products ?? 0,
          customerRating: data.stats?.customer_rating ?? 0,
        });
        setLowStockProducts(data.low_stock_products || []);
        setRecentOrders(data.recent_orders || []);
        setSupportTickets(data.support_tickets || []);
        setTopProducts(data.top_products || []);
      })
      .catch((error) => {
        console.log(error);
        Swal.fire("Error", "Failed to load dashboard data", "error");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getDashboardStats();
  }, []);

  const handleRestock = async (productId) => {
    const { value: quantity } = await Swal.fire({
      title: "Restock Product",
      input: "number",
      inputLabel: "Enter quantity to add",
      inputPlaceholder: "Quantity",
      showCancelButton: true,
      confirmButtonColor: "#097d43",
      cancelButtonColor: "#6b7280",
      inputValidator: (value) => {
        if (!value || value <= 0) {
          return "Please enter a valid quantity!";
        }
      },
    });

    if (quantity) {
      const formData = new FormData();
      formData.append("id", productId);
      formData.append("quantity", quantity);

      axios
        .post(`${baseUrl}/seller/product/restockProduct`, formData)
        .then((response) => {
          if (response.data.success == true) {
            Swal.fire("Success!", `Stock updated to ${response.data.stock}`, "success");
            getDashboardStats();
          } else {
            Swal.fire("Error", "Failed to restock product", "error");
          }
        })
        .catch((error) => {
          console.log(error);
          Swal.fire("Error", "Failed to restock product", "error");
        });
    }
  };

  const handleViewProduct = (productId) => {
    // Navigate to product detail or open modal
    console.log("View product:", productId);
  };

  const getStatusClass = (status) => {
    return `dash-status-${status.toLowerCase()}`;
  };

  const getStockStatusClass = (status) => {
    return status === "critical" ? "dash-stock-critical" : "dash-stock-low";
  };

  const getPriorityClass = (priority) => {
    return `dash-priority-${priority}`;
  };

  // Pagination for Low Stock
  const indexOfLastItem = lowStockPage * lowStockPerPage;
  const indexOfFirstItem = indexOfLastItem - lowStockPerPage;
  const currentLowStock = lowStockProducts.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  return (
    <SellerLayout>
      <div className="dash-container">
        <Container fluid>
          {/* KYC Intro Modal — loading se independent, hamesha mount rehti hai */}
          <VerificationModal
            show={showVerificationModal}
            onHide={() => setShowVerificationModal(false)}
            onSkip={() => setShowVerificationModal(false)}
          />

          {loading ? (
            <SellerDashboardSkeleton />
          ) : (
            <>
              {/* Welcome Header */}
              <div className="dash-header">
                <div>
                  <h1>Welcome back, {loggedInSeller?.name}!</h1>
                  <p>Here's what's happening today</p>
                </div>
              </div>

          {/* Stats Cards */}
          <Row className="dash-stats g-3 g-lg-4">
            {stats.map((stat, index) => (
              <Col key={index} xs={12} sm={6} xl={3}>
                <div className="dash-stat-card">
                  <div className="dash-stat-content">
                    <div className="dash-stat-label">{stat.label}</div>
                    <div className="dash-stat-value">{stat.value}</div>
                  </div>
                  <div
                    className="dash-stat-icon"
                    style={{ backgroundColor: stat.bgColor }}
                  >
                    {stat.icon}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          {/* Low Stock Alerts Table */}
          <div className="dash-low-stock-section">
            <div className="dash-section-header">
              <h3>
                <FaExclamationTriangle /> Low Stock Alerts
              </h3>
              <Link to="/seller/products" className="dash-view-all">
                View All →
              </Link>
            </div>
            <div className="dash-table-wrapper">
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>PRODUCT NAME</th>
                    <th>SKU</th>
                    <th>CATEGORY</th>
                    <th>CURRENT STOCK</th>
                    <th>MIN STOCK</th>
                    <th>PRICE</th>
                    <th>STATUS</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {currentLowStock.map((product) => (
                    <tr key={product.id}>
                      <td className="dash-product-name">{product.name}</td>
                      <td>{product.sku}</td>
                      <td>{product.category}</td>
                      <td className="dash-stock-current">{product.stock}</td>
                      <td>{product.minStock}</td>
                      <td className="dash-price">{product.price}</td>
                      <td>
                        <span
                          className={`dash-stock-badge ${getStockStatusClass(product.status)}`}
                        >
                          {product.status === "critical" ? "Critical" : "Low"}
                        </span>
                      </td>
                      <td>
                        <ActionDropdown
                          isOpen={openActionMenu === product.id}
                          onToggle={() =>
                            setOpenActionMenu(
                              openActionMenu === product.id ? null : product.id,
                            )
                          }
                          actions={[
                            {
                              label: "Restock",
                              icon: <FaPlus />,
                              onClick: () => handleRestock(product.id),
                            },
                            {
                              label: "Edit Product",
                              icon: <FaEdit />,
                              onClick: () => handleViewProduct(product.id),
                            },
                            {
                              label: "View Details",
                              icon: <FaEye />,
                              onClick: () => handleViewProduct(product.id),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              totalItems={lowStockProducts.length}
              itemsPerPage={lowStockPerPage}
              currentPage={lowStockPage}
              onPageChange={setLowStockPage}
              onItemsPerPageChange={(items) => {
                setLowStockPerPage(items);
                setLowStockPage(1);
              }}
            />
          </div>
          {/* Recent Orders & Support Tickets Row */}
          <Row className="g-3 g-lg-4 ">
            {/* Recent Orders */}
            <Col xs={12} lg={6}>
              <div className="dash-section-card">
                <div className="dash-section-header">
                  <h3>Recent Orders</h3>
                  <Link to="/seller/orders" className="dash-view-all">
                    View All →
                  </Link>
                </div>
                <div className="dash-compact-table">
                  <table>
                    <thead>
                      <tr>
                        <th>ORDER ID</th>
                        <th>CUSTOMER</th>
                        <th>AMOUNT</th>
                        <th>STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.map((order) => (
                        <tr key={order.id}>
                          <td className="dash-order-id">{order.id}</td>
                          <td>{order.customer}</td>
                          <td className="dash-amount">
                            PKR {Number(order.amount).toLocaleString()}
                          </td>
                          <td>
                            <span
                              className={`dash-status ${getStatusClass(order.status)}`}
                            >
                              {order.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Col>

            {/* Support Tickets */}
            <Col xs={12} lg={6}>
              <div className="dash-section-card">
                <div className="dash-section-header">
                  <h3>
                    <FaTicketAlt /> Recent Support Tickets
                  </h3>
                  <Link to="/seller/support" className="dash-view-all">
                    View All →
                  </Link>
                </div>
                <div className="dash-compact-table">
                  <table>
                    <thead>
                      <tr>
                        <th>TICKET ID</th>
                        <th>SUBJECT</th>
                        <th>PRIORITY</th>
                        <th>TIME</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supportTickets.map((ticket) => (
                        <tr key={ticket.id}>
                          <td className="dash-ticket-id">{ticket.id}</td>
                          <td className="dash-ticket-subject">
                            {ticket.subject}
                          </td>
                          <td>
                            <span
                              className={`dash-priority ${getPriorityClass(ticket.priority)}`}
                            >
                              {ticket.priority}
                            </span>
                          </td>
                          <td className="dash-time">{ticket.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Col>
          </Row>

          {/* Top Selling Products Table */}
          <div className="dash-top-products-table">
            <div className="dash-section-header">
              <h3>Top Selling Products</h3>
              <Link to="/seller/products" className="dash-view-all">
                View All →
              </Link>
            </div>
            <div className="dash-table-wrapper">
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>PRODUCT NAME</th>
                    <th>SOLD</th>
                    <th>RATING</th>
                    <th>REVENUE</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((product, index) => (
                    <tr key={index}>
                      <td className="dash-rank">{index + 1}</td>
                      <td className="dash-product-name">{product.name}</td>
                      <td>
                        <span className="dash-sold-count">
                          <FaShoppingBag /> {product.sold}
                        </span>
                      </td>
                      <td>
                        <span className="dash-rating">
                          <FaStar /> {product.rating}
                        </span>
                      </td>
                      <td className="dash-revenue">{product.revenue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
            </>
          )}
        </Container>
      </div>
    </SellerLayout>
  );
};

export default SellerDashboard;
